import React, { useEffect, useState } from "react";

//** NDBX */
import {
	CurrencyInputCard,
	RHFCircleToggleGroupLabelled,
	RHFRadioGroup,
	Section,
	RHFCheckbox,
} from "@arp/react-ndbx";

//** Config */
import { selectCoverFieldNames } from "../../../config/pageInfo/pageFieldName.js";

import {
	CRITICAL_ILLNESS_MIN,
	CRITICAL_ILLNESS_MAX,
	PERMANENT_DISABILITY_MIN,
	PERMANENT_DISABILITY_MAX,
	LIFE_INSURANCE_MIN,
	LIFE_INSURANCE_MAX,
} from "../../../schema/sharedConstValues.js";

import { selectCoverDisclaimers } from "../../../config/disclaimers/disclaimers.jsx";
import {
	questionTypes,
	questionLabels,
} from "../../../config/questions/questionItems.js";
import { selectCoverTooltips } from "../../../config/tooltips/pageTooltips";

//** Utilities  */
import {
	formatCurrencyNoDecimal,
	valueWithinRange,
	currencyStringToNumber,
} from "../../../utilities/numericFormat.js";

//** MUI */
import { Box, Typography, Divider } from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";

//** Context */
import usePreQualification from "../../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages";
import { useGetReferenceData } from "../../../hooks/dataHooks/useGetReferenceData.js";
import useSharedData from "../../../hooks/useSharedData.ts";
import { useWatch } from "react-hook-form";
import { useOutletContext } from "react-router-dom";

//----------------------------------------------------------------

export default function SelectCoverDetails({ ...props }) {
	const {
		person,
		applicantTitle,
		isOnline = true,
	} = {
		...props,
	};

	const { data, isLoading } = useGetReferenceData();
	const { occupationTypeList } = isLoading === false ? data : {};

	if (isLoading === true) {
		return;
	}

	const { handleEventChange } = useSharedData();
	const { dataSet, funcSet } = usePreQualification();
	const { currentPage } = usePreQualificationPages();

	const {
		methods: [methods],
		showSecond: [showSecond],
		validateTrauma: [validateTrauma],
		validateOccupation: [validateOccupation],
	} = useOutletContext();
	const { control, setValue, clearErrors, resetField, trigger, reset } = {
		...methods,
	};
	const theme = useTheme();
	const [currentPerson, setCurrentPerson] = useState({});
	const [showAllTraumaCover, setShowAllTraumaCover] = useState(false);
	const [showOccupationTypeCover, setShowOccupationTypeCover] = useState(false);

	//* Watch for changes in the Life Cover amount
	// let criticalIllnessWatch = "";
	// let permanentDisabilityWatch = "";

	const lifeCoverWatch = useWatch({
		control,
		name: selectCoverFieldNames(person).LIFE_COVER,
	});

	let criticalIllnessWatch = useWatch({
		control,
		name: selectCoverFieldNames(person).CRITICAL_ILLNESS_COVER,
	});

	let permanentDisabilityWatch = useWatch({
		control,
		name: selectCoverFieldNames(person).PERMANENT_DISABILITY_COVER,
	});

	useEffect(() => {
		// Check if there is a value in Critical Illness
		// and check Permanently Unable to Work
		cleanUpCoverEvents(dataSet[person]);

		if (dataSet[person]) {
			setCurrentPerson(dataSet[person]);
		}

		//* Let the layout page know we are doing updates.
		handleEventChange({
			name: "updatingQuoteSummary",
			value: false,
		});
	}, []);

	const cleanUpCoverEvents = (personDetails) => {
		if (personDetails.illnessCover === "") {
			criticalIllnessWatch = "";
			resetField(selectCoverFieldNames(person).CRITICAL_ILLNESS_COVER);
			setShowAllTraumaCover(false);
			validateTrauma(false, person);
			setValue(selectCoverFieldNames(person).TRAUMA_COVER, "");
			clearErrors(selectCoverFieldNames(person).TRAUMA_COVER);
		}
		if (personDetails.permanentDisabilityCover === "") {
			permanentDisabilityWatch = "";
			resetField(selectCoverFieldNames(person).PERMANENT_DISABILITY_COVER);
			setShowOccupationTypeCover(false);
			validateOccupation(false, person);
			setValue(selectCoverFieldNames(person).OCCUPATION_LIST, "");
			clearErrors(selectCoverFieldNames(person).OCCUPATION_LIST);
		}
	};

	//*
	const isCriticalIllnessValid = () => {
		const validField = Number(lifeCoverWatch) >= Number(criticalIllnessWatch);
		const validFieldTwo = valueWithinRange(
			Number(criticalIllnessWatch),
			CRITICAL_ILLNESS_MIN[dataSet[person].minAgeGroup],
			CRITICAL_ILLNESS_MAX[dataSet[person].maxAgeGroup]
		);

		const validCombo = validField && validFieldTwo;
		return validCombo;
	};

	useEffect(() => {
		if (criticalIllnessWatch == null) return;
		const validCombo = isCriticalIllnessValid();

		if (validCombo === true) {
			setShowAllTraumaCover(true);
			validateTrauma(true, person);
		} else {
			setShowAllTraumaCover(false);
			validateTrauma(false, person);
			//* when you don't show the trauma cover set its value and error to empty
			setValue(selectCoverFieldNames(person).TRAUMA_COVER, "");
			clearErrors(selectCoverFieldNames(person).TRAUMA_COVER);

			//* Reset the CI value in RHF if we are not showing the CI
			if (dataSet[person].showAdditionalCover === false) {
				resetField(selectCoverFieldNames(person).CRITICAL_ILLNESS_COVER, {
					defaultValue: "",
				});
				resetField(selectCoverFieldNames(person).TRAUMA_COVER, {
					defaultValue: "",
				});
			}
			if (!criticalIllnessWatch) {
				//* Remove the CI from the dataSet state object
				const updateField = person + "_illnessCoverPremium";
				const updateQuestion = person + "_traumaCover";

				let updateObject = {
					[updateField]: "",
					[updateQuestion]: "",
					type: "remove",
					person: person,
				};

				funcSet.updateCriticalIllness(updateObject);
			}
			//* Trigger validation
			if (currentPage?.valid) {
				trigger();
			}
		}
	}, [criticalIllnessWatch]);

	//*
	const isPUWValid = () => {
		const validField =
			Number(lifeCoverWatch) >= Number(permanentDisabilityWatch);
		const validFieldTwo = valueWithinRange(
			permanentDisabilityWatch,
			PERMANENT_DISABILITY_MIN[dataSet[person].minAgeGroup],
			PERMANENT_DISABILITY_MAX[dataSet[person].maxAgeGroup]
		);

		const validCombo = validField && validFieldTwo;

		return validCombo;
	};
	useEffect(() => {
		if (permanentDisabilityWatch == null) return;
		const validCombo = isPUWValid();

		if (validCombo === true) {
			setShowOccupationTypeCover(true);
			validateOccupation(true, person);
		} else {
			setShowOccupationTypeCover(false);
			validateOccupation(false, person);
			setValue(selectCoverFieldNames(person).OCCUPATION_LIST, "");
			clearErrors(selectCoverFieldNames(person).OCCUPATION_LIST);

			//* Reset the PUW value in RHF if we are not showing the PUW
			if (dataSet[person].showAdditionalCover === false) {
				resetField(selectCoverFieldNames(person).PERMANENT_DISABILITY_COVER, {
					defaultValue: "",
				});
				resetField(selectCoverFieldNames(person).OCCUPATION_LIST, {
					defaultValue: "",
				});
			}

			if (!permanentDisabilityWatch) {
				//* Remove the PUW from the dataSet state object
				const updateField = person + "_permanentDisabilityCoverPremium";
				let updateObject = {
					[updateField]: "",
					type: "remove",
					person: person,
				};

				funcSet.updatePermanentDisabilityCover(updateObject);
			}
			//* Trigger validation
			if (currentPage?.valid) {
				trigger();
			}
		}
	}, [permanentDisabilityWatch]);

	useEffect(() => {
		if (showSecond === false) {
			resetField(selectCoverFieldNames("personTwo").LIFE_COVER, {
				defaultValue: "",
			});
			resetField(selectCoverFieldNames("personTwo").CRITICAL_ILLNESS_COVER, {
				defaultValue: "",
			});
			resetField(selectCoverFieldNames("personTwo").TRAUMA_COVER, {
				defaultValue: "",
			});
			resetField(
				selectCoverFieldNames("personTwo").PERMANENT_DISABILITY_COVER,
				{
					defaultValue: "",
				}
			);
			resetField(selectCoverFieldNames("personTwo").OCCUPATION_LIST, {
				defaultValue: "",
			});
			resetField(selectCoverFieldNames("personTwo").TERMS_AND_CONDITIONS, {
				defaultValue: false,
			});
		}
	}, [showSecond]);

	//! ----------------------------------------------------------------
	const [lifeCoverAmountComparison, setLifeCoverAmountComparison] =
		useState(null);
	const [criticalIllnessLessThan, setCriticalIllnessLessThan] = useState(false);
	const [unableToWorkLessThan, setUnableToWorkLessThan] = useState(false);

	const handleLifeCoverOnBlur = (e) => {
		if (!e) return;
		const { value } = e?.target;

		const valueAsInteger =
			value === "" ? "" : parseFloat(currencyStringToNumber(value), 10);

		setLifeCoverAmountComparison(valueAsInteger);
	};

	useEffect(() => {
		if (lifeCoverAmountComparison == null || lifeCoverAmountComparison === "")
			return;

		if (criticalIllnessWatch != null) {
			isCILessThanLifeCheck();
		}

		if (permanentDisabilityWatch != null) {
			isPUWLessThanLifeCheck();
		}
	}, [lifeCoverAmountComparison]);

	const isCILessThanLifeCheck = () => {
		//* Check CI value is less than Life value
		const cILessThan =
			Number(lifeCoverAmountComparison) < Number(criticalIllnessWatch)
				? true
				: false;

		setCriticalIllnessLessThan(cILessThan);
		// setShowAllTraumaCover(!cILessThan);
	};

	const isPUWLessThanLifeCheck = () => {
		//* Check PUW value is less than Life value
		const puwLessThan =
			Number(lifeCoverAmountComparison) < Number(permanentDisabilityWatch)
				? true
				: false;
		setUnableToWorkLessThan(puwLessThan);
	};

	useEffect(() => {
		//* Is the CI less than the Life Cover.
		if (criticalIllnessLessThan === true) {
			trigger(selectCoverFieldNames(person).CRITICAL_ILLNESS_COVER);
			setValue(selectCoverFieldNames(person).TRAUMA_COVER, "");
			clearErrors(selectCoverFieldNames(person).TRAUMA_COVER);
			setShowAllTraumaCover(false);
			validateTrauma(false, person);
		} else {
			clearErrors(selectCoverFieldNames(person).CRITICAL_ILLNESS_COVER);
		}
	}, [criticalIllnessLessThan]);

	useEffect(() => {
		//* Is PUW less than Life Cover.
		if (unableToWorkLessThan === true) {
			trigger(selectCoverFieldNames(person).PERMANENT_DISABILITY_COVER);
			setValue(selectCoverFieldNames(person).OCCUPATION_LIST, "");
			clearErrors(selectCoverFieldNames(person).OCCUPATION_LIST);
			setShowOccupationTypeCover(false);
			validateOccupation(false, person);
		} else {
			clearErrors(selectCoverFieldNames(person).PERMANENT_DISABILITY_COVER);
		}
	}, [unableToWorkLessThan]);

	useEffect(() => {
		//** Set the form values if we are coming from the policy retrieval */
		if (isLoading || dataSet?.customerRetrieveQuoteLoaded === false) return;

		setValue(
			selectCoverFieldNames(person).LIFE_COVER,
			dataSet[person].lifeCover,
			{ shouldDirty: true }
		);

		const hasCriticalIllnessCover =
			!isNaN(dataSet[person]?.illnessCover) &&
			dataSet[person]?.illnessCover?.toString().length > 0;

		setShowAllTraumaCover(hasCriticalIllnessCover);

		if (hasCriticalIllnessCover === true) {
			setValue(
				selectCoverFieldNames(person).CRITICAL_ILLNESS_COVER,
				dataSet[person].illnessCover,
				{ shouldDirty: true }
			);

			setValue(
				selectCoverFieldNames(person).TRAUMA_COVER,
				dataSet[person].traumaCover,
				{ shouldDirty: true }
			);
		}

		const hasPermanentDisabilityCover =
			!isNaN(dataSet[person]?.permanentDisabilityCover) &&
			dataSet[person]?.permanentDisabilityCover?.toString().length > 0;

		setShowOccupationTypeCover(hasPermanentDisabilityCover);

		if (hasPermanentDisabilityCover === true) {
			setValue(
				selectCoverFieldNames(person).PERMANENT_DISABILITY_COVER,
				dataSet[person].permanentDisabilityCover,
				{ shouldDirty: true }
			);
			setValue(
				selectCoverFieldNames(person).OCCUPATION_LIST,
				dataSet[person].occupationList,
				{ shouldDirty: true }
			);
		}
		setValue(
			selectCoverFieldNames(person).TERMS_AND_CONDITIONS,
			dataSet[person].termsAndConditions,
			{ shouldDirty: true }
		);
	}, [dataSet?.customerRetrieveQuoteLoaded]);

	//* We need to make sure that we validate the
	//* CI and PUW regardless of the order the
	//* values are entered.
	useEffect(() => {
		const lifeCoverInRange = valueWithinRange(
			lifeCoverWatch,
			LIFE_INSURANCE_MIN[dataSet[person].minAgeGroup],
			LIFE_INSURANCE_MAX[dataSet[person].maxAgeGroup]
		);

		if (lifeCoverInRange === false) return;

		const validCICombo = isCriticalIllnessValid();
		setShowAllTraumaCover(validCICombo);
		validateTrauma(validCICombo, person);
		if (validCICombo) {
			trigger(selectCoverFieldNames(person).CRITICAL_ILLNESS_COVER);
			trigger(selectCoverFieldNames(person).TRAUMA_COVER);
		}

		const validPUWCombo = isPUWValid();
		setShowOccupationTypeCover(validPUWCombo);
		validateOccupation(validPUWCombo, person);
		if (validPUWCombo) {
			trigger(selectCoverFieldNames(person).PERMANENT_DISABILITY_COVER);
			trigger(selectCoverFieldNames(person).OCCUPATION_LIST);
		}
	}, [lifeCoverWatch]);

	//! ----------------------------------------------------------------

	let applicantFullName = currentPerson
		? currentPerson?.firstName + " " + currentPerson?.surname
		: "Applicant 1";
	//----------------------------------------------------------------

	return (
		<>
			{showSecond === true ? (
				<Section
					data-identifier="first-applicant-name"
					sx={{
						textAlign: { xs: "left", sm: "center" },
						ml: { xs: 1, sm: 0 },
						mb: { xs: 2 },
					}}
				>
					<Typography
						sx={{ textAlign: "left", whiteSpace: "nowrap" }}
						variant="h5-bold"
					>
						Applicant {applicantTitle} - {currentPerson.firstName}{" "}
						{currentPerson.surname}
					</Typography>
				</Section>
			) : null}
			<Section mb={{ xs: 2, sm: 5 }}>
				<CurrencyInputCard
					name={selectCoverFieldNames(person).LIFE_COVER}
					defaultValue={dataSet[person].lifeCover}
					title="Life cover"
					subTitle={`Please select cover amount between <b> ${formatCurrencyNoDecimal(
						LIFE_INSURANCE_MIN[dataSet[person].minAgeGroup]
					)} and  ${formatCurrencyNoDecimal(
						LIFE_INSURANCE_MAX[dataSet[person].maxAgeGroup]
					)} </b>`}
					placeholder="Cover amount"
					label="Cover amount"
					backgroundColor={`${theme.palette.soft.softBeige150}`}
					className="text-field-bold"
					{...(!isOnline && { tooltipWidth: 700 })}
					tooltipText={
						isOnline
							? selectCoverTooltips.LIFE_COVER_TOOLTIP
							: selectCoverTooltips.LIFE_COVER_TOOLTIP_CONTACT_CENTRE
					}
					onBlur={(e) => {
						handleLifeCoverOnBlur(e);
					}}
				/>
			</Section>
			{dataSet[person].showAdditionalCover === true ? (
				<>
					<Section
						sx={{ backgroundColor: theme.palette.greyscale.Grey600 }}
						mt={0}
						mb={{ xs: 2, sm: 5 }}
					>
						<CurrencyInputCard
							name={selectCoverFieldNames(person).CRITICAL_ILLNESS_COVER}
							defaultValue={dataSet[person].illnessCover}
							title="Critical Illness cover (optional)"
							subTitle={`Please select cover amount between <b>${formatCurrencyNoDecimal(
								CRITICAL_ILLNESS_MIN[dataSet[person].minAgeGroup]
							)} and ${formatCurrencyNoDecimal(
								CRITICAL_ILLNESS_MAX[dataSet[person].maxAgeGroup]
							)}</b>`}
							placeholder="Cover amount"
							label="Cover amount"
							backgroundColor={`${theme.palette.greyscale.Grey600}`}
							className="text-field-bold"
							{...(!isOnline && { tooltipWidth: 700 })}
							tooltipText={
								isOnline
									? selectCoverTooltips.CRITICAL_ILLNESS_TOOLTIP
									: selectCoverTooltips.CRITICAL_ILLNESS_TOOLTIP_CONTACT_CENTRE
							}
						/>
						{showAllTraumaCover ? (
							<Section
								textAlign="left"
								sx={{ px: 3, pb: 3, maxWidth: { xs: "690px" } }}
								mb={0}
								mt={0}
							>
								<RHFCircleToggleGroupLabelled
									variant="circle-group"
									name={selectCoverFieldNames(person).TRAUMA_COVER}
									items={questionTypes.YES_NO}
									label={{ text: questionLabels.TRAUMA_COVER_QUESTION.label }}
									description={
										isOnline
											? questionLabels.TRAUMA_COVER_QUESTION.description.online
											: questionLabels.TRAUMA_COVER_QUESTION.description
													.contactCentre
									}
									position={{ xs: "center", sm: "left" }}
									minHeight={0}
								/>
							</Section>
						) : null}
					</Section>
					<Section
						sx={{ backgroundColor: theme.palette.greyscale.Grey600 }}
						mt={0}
						mb={{ xs: 2, sm: 5 }}
					>
						<CurrencyInputCard
							name={selectCoverFieldNames(person).PERMANENT_DISABILITY_COVER}
							defaultValue={dataSet[person].permanentDisabilityCover}
							title="Permanently unable to work cover (optional)"
							subTitle={`Please select cover amount between <b>${formatCurrencyNoDecimal(
								PERMANENT_DISABILITY_MIN[dataSet[person].minAgeGroup]
							)} and ${formatCurrencyNoDecimal(
								PERMANENT_DISABILITY_MAX[dataSet[person].maxAgeGroup]
							)}</b>`}
							placeholder="Cover amount"
							label="Cover amount"
							backgroundColor={`${theme.palette.greyscale.Grey600}`}
							className="text-field-bold"
							{...(!isOnline && { tooltipWidth: 700 })}
							tooltipText={
								isOnline
									? selectCoverTooltips.PERMANENT_DISABILITY_TOOLTIP
									: selectCoverTooltips.PERMANENT_DISABILITY_TOOLTIP_CONTACT_CENTRE
							}
						/>
						{showOccupationTypeCover && (
							<Section
								textAlign="left"
								sx={{ maxWidth: { xs: "690px" }, px: 3, pb: 3 }}
								mt={0}
								mb={0}
							>
								<RHFRadioGroup
									name={selectCoverFieldNames(person).OCCUPATION_LIST}
									defaultValue={dataSet[person].occupationList}
									label="Occupation Type"
									items={isLoading === false ? occupationTypeList : {}}
									radioPosition="flex-start"
								/>
							</Section>
						)}
					</Section>
				</>
			) : null}
			<Section
				sx={{ maxWidth: { xs: "690px" }, px: 3 }}
				textAlign="left"
				mb={5}
				mt={0}
			>
				{dataSet[person].showAdditionalCover === true && (
					<Box mb={2} id="optional_cover_disclaimer">
						<Typography variant="p3-bold">
							{selectCoverDisclaimers().OPTIONAL_COVER_DISCLAIMER.title}
						</Typography>
						<Typography variant="p3">
							{isOnline
								? selectCoverDisclaimers().OPTIONAL_COVER_DISCLAIMER.note
								: selectCoverDisclaimers()
										.OPTIONAL_COVER_DISCLAIMER_CONTACT_CENTRE.note}
						</Typography>
					</Box>
				)}
				{isOnline && (
					<>
						<Box
							id="privacy_notice_disclaimer"
							sx={{
								borderTop: `1px solid ${theme.palette.greyscale.Grey550}`,
								paddingTop: 5,
							}}
						>
							<Box mb={{ xs: 2, md: 3 }}>
								<Typography variant="p3">
									{selectCoverDisclaimers().PRIVACY_NOTICE_DISCLAIMER.title}
								</Typography>
							</Box>

							<Box mb={{ xs: 2, md: 3 }}>
								<Typography variant="p3">
									{selectCoverDisclaimers().PRIVACY_NOTICE_DISCLAIMER.note}
								</Typography>
							</Box>
							<Box id="misrepresentation_disclaimer" mb={{ xs: 2, md: 3 }}>
								<Typography variant="p3">
									{selectCoverDisclaimers().MISREPRESENTATION_DISCLAIMER.note}
								</Typography>
							</Box>
							<Box
								mb={{ xs: 2, md: 3 }}
								id="misrepresentation_disclaimer_sub_note"
							>
								<Typography variant="p3">
									{
										selectCoverDisclaimers().MISREPRESENTATION_DISCLAIMER
											.sunNote
									}
								</Typography>
							</Box>
							<Box id="product_disclosure_disclaimer">
								<Typography variant="p3">
									{selectCoverDisclaimers().PRODUCT_DISCLOSURE_DISCLAIMER.note}
								</Typography>
							</Box>
						</Box>

						<Section ml={-2} mt={showSecond === true ? 4 : 4}>
							{showSecond === true && (
								<Section
									data-identifier="second-applicant-name"
									sx={{
										textAlign: { xs: "left", sm: "left" },
										ml: { xs: 1, sm: 0 },
										mb: { xs: 2 },
									}}
								>
									<Typography
										variant="h5-bold"
										ml={{ xs: 0, sm: 6 }}
										sx={{ textAlign: "left", whiteSpace: "nowrap" }}
									>
										Applicant {applicantTitle} - {currentPerson.firstName}{" "}
										{currentPerson.surname}
									</Typography>
								</Section>
							)}
							<Section ml={{ xs: -2, sm: 0 }}>
								<RHFCheckbox
									label={applicantFullName}
									defaultChecked={dataSet[person].termsAndConditions}
									name={selectCoverFieldNames(person).TERMS_AND_CONDITIONS}
								></RHFCheckbox>
							</Section>
						</Section>
					</>
				)}
				<Divider />
			</Section>
		</>
	);
}
